import React, { useState } from 'react';
import { X, ArrowRight, CheckCircle, BookOpen, Headphones, Target, FileText } from 'lucide-react';

interface LearningStyleAssessmentProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (style: string, preferences: any) => void;
  currentStyle?: string;
}

interface Question {
  id: string;
  question: string;
  options: Array<{
    id: string;
    text: string;
    styles: string[];
  }>;
}

const LearningStyleAssessment: React.FC<LearningStyleAssessmentProps> = ({
  isOpen,
  onClose,
  onComplete,
  currentStyle
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isCompleted, setIsCompleted] = useState(false);

  const questions: Question[] = [
    {
      id: 'preference-1',
      question: 'When learning something new, I prefer to:',
      options: [
        { id: 'a', text: 'Read detailed explanations and take notes', styles: ['reading_writing'] },
        { id: 'b', text: 'Watch videos or see diagrams', styles: ['visual'] },
        { id: 'c', text: 'Listen to explanations or discussions', styles: ['auditory'] },
        { id: 'd', text: 'Try it out hands-on or practice', styles: ['kinesthetic'] }
      ]
    },
    {
      id: 'preference-2',
      question: 'I learn best when I can:',
      options: [
        { id: 'a', text: 'See charts, graphs, and visual aids', styles: ['visual'] },
        { id: 'b', text: 'Hear clear explanations and examples', styles: ['auditory'] },
        { id: 'c', text: 'Write down key points and organize them', styles: ['reading_writing'] },
        { id: 'd', text: 'Practice and experiment with the material', styles: ['kinesthetic'] }
      ]
    },
    {
      id: 'preference-3',
      question: 'When studying, I like to:',
      options: [
        { id: 'a', text: 'Use flashcards and written summaries', styles: ['reading_writing'] },
        { id: 'b', text: 'Create mind maps and visual diagrams', styles: ['visual'] },
        { id: 'c', text: 'Discuss with others or explain out loud', styles: ['auditory'] },
        { id: 'd', text: 'Use physical objects or role-play', styles: ['kinesthetic'] }
      ]
    },
    {
      id: 'preference-4',
      question: 'I remember information better when:',
      options: [
        { id: 'a', text: 'I can see it written down or in pictures', styles: ['visual', 'reading_writing'] },
        { id: 'b', text: 'I hear it explained or discussed', styles: ['auditory'] },
        { id: 'c', text: 'I can practice it or do it myself', styles: ['kinesthetic'] },
        { id: 'd', text: 'I can organize it in lists or outlines', styles: ['reading_writing'] }
      ]
    },
    {
      id: 'preference-5',
      question: 'When I need to understand something complex, I:',
      options: [
        { id: 'a', text: 'Draw diagrams or create visual representations', styles: ['visual'] },
        { id: 'b', text: 'Talk through it with someone else', styles: ['auditory'] },
        { id: 'c', text: 'Write detailed notes and summaries', styles: ['reading_writing'] },
        { id: 'd', text: 'Try to build or model it physically', styles: ['kinesthetic'] }
      ]
    }
  ];

  const learningStyles = {
    visual: {
      name: 'Visual Learner',
      description: 'You learn best through images, diagrams, charts, and visual aids.',
      icon: BookOpen,
      color: 'blue',
      tips: [
        'Use color coding and highlighting',
        'Create mind maps and diagrams',
        'Watch videos and visual presentations',
        'Use flashcards with images'
      ]
    },
    auditory: {
      name: 'Auditory Learner',
      description: 'You learn best through listening, discussions, and verbal explanations.',
      icon: Headphones,
      color: 'green',
      tips: [
        'Listen to podcasts and audio content',
        'Discuss topics with others',
        'Read aloud or use text-to-speech',
        'Use rhymes and mnemonics'
      ]
    },
    reading_writing: {
      name: 'Reading/Writing Learner',
      description: 'You learn best through reading, writing, and text-based materials.',
      icon: FileText,
      color: 'purple',
      tips: [
        'Take detailed notes',
        'Create written summaries',
        'Use lists and bullet points',
        'Read extensively on topics'
      ]
    },
    kinesthetic: {
      name: 'Kinesthetic Learner',
      description: 'You learn best through hands-on activities and physical movement.',
      icon: Target,
      color: 'orange',
      tips: [
        'Use interactive exercises',
        'Practice with real examples',
        'Take breaks to move around',
        'Use role-playing and simulations'
      ]
    }
  };

  const handleAnswer = (questionId: string, answerId: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      calculateLearningStyle();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateLearningStyle = () => {
    const styleScores: Record<string, number> = {
      visual: 0,
      auditory: 0,
      reading_writing: 0,
      kinesthetic: 0
    };

    Object.values(answers).forEach(answerId => {
      const question = questions.find(q => 
        Object.values(answers).indexOf(answerId) !== -1
      );
      if (question) {
        const option = question.options.find(opt => opt.id === answerId);
        if (option) {
          option.styles.forEach(style => {
            styleScores[style] = (styleScores[style] || 0) + 1;
          });
        }
      }
    });

    // Find the style with the highest score
    const primaryStyle = Object.entries(styleScores)
      .sort(([,a], [,b]) => b - a)[0][0];

    setIsCompleted(true);
    onComplete(primaryStyle, {
      scores: styleScores,
      answers,
      primaryStyle,
      secondaryStyle: Object.entries(styleScores)
        .sort(([,a], [,b]) => b - a)[1][0]
    });
  };

  const resetAssessment = () => {
    setCurrentStep(0);
    setAnswers({});
    setIsCompleted(false);
  };

  if (!isOpen) return null;

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Learning Style Assessment</h2>
              <p className="text-gray-600 mt-1">
                {isCompleted ? 'Your learning style results' : `Question ${currentStep + 1} of ${questions.length}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          
          {!isCompleted && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {!isCompleted ? (
            <>
              {/* Question */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  {currentQuestion.question}
                </h3>
                
                <div className="space-y-3">
                  {currentQuestion.options.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleAnswer(currentQuestion.id, option.id)}
                      className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-200 ${
                        answers[currentQuestion.id] === option.id
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          answers[currentQuestion.id] === option.id
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {answers[currentQuestion.id] === option.id && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className="font-medium">{option.text}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                <button
                  onClick={handleNext}
                  disabled={!answers[currentQuestion.id]}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {currentStep === questions.length - 1 ? 'Get Results' : 'Next'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            /* Results */
            <div className="text-center">
              <div className="mb-8">
                {(() => {
                  const style = learningStyles[answers[questions[0].id] ? 
                    Object.entries(learningStyles).find(([key]) => 
                      Object.values(answers).some(answerId => {
                        const question = questions.find(q => 
                          Object.values(answers).indexOf(answerId) !== -1
                        );
                        return question?.options.find(opt => opt.id === answerId)?.styles.includes(key);
                      })
                    )?.[0] || 'visual' : 'visual'
                  ];
                  
                  const IconComponent = style.icon;
                  
                  return (
                    <>
                      <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-${style.color}-100 flex items-center justify-center`}>
                        <IconComponent className={`w-10 h-10 text-${style.color}-600`} />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{style.name}</h3>
                      <p className="text-gray-600 mb-6">{style.description}</p>
                    </>
                  );
                })()}
              </div>

              <div className="text-left mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Learning Tips for You:</h4>
                <ul className="space-y-2">
                  {(() => {
                    const style = learningStyles[answers[questions[0].id] ? 
                      Object.entries(learningStyles).find(([key]) => 
                        Object.values(answers).some(answerId => {
                          const question = questions.find(q => 
                            Object.values(answers).indexOf(answerId) !== -1
                          );
                          return question?.options.find(opt => opt.id === answerId)?.styles.includes(key);
                        })
                      )?.[0] || 'visual' : 'visual'
                    ];
                    
                    return style.tips.map((tip, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{tip}</span>
                      </li>
                    ));
                  })()}
                </ul>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={resetAssessment}
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Retake Assessment
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningStyleAssessment;
